<template>
    <WorkScheduleContainerRow stickyTop>
        <WorkScheduleContainerColumn headerColumn stickyLeft>{{ label }}</WorkScheduleContainerColumn>
        <WorkScheduleContainerColumn noPadding class="timeBar" v-if="hasRenderedDays">
            <div class="placeHolderLeft"></div>
            <template v-for="(renderedDay, dayLabel) in renderedDays" :key="'day-block' + dayLabel">
                <div :class="dayCssClass(renderedDay)" :style="{'--day-block-colum-span': getHoursInDay(renderedDay)}"><span>{{ renderedDay.label }}</span></div>
                <template v-for="(column, i) in renderedDay.columns" :key="'time-bar-block' + i + '_' + dayLabel">
                    <div class="timeBlock start" :class="{'startOfDay': i === 1}">{{ column.begin }}</div>
                    <div class="timeBlock end" :class="{'startOfDay': i === 1}">{{ column.end }}</div>
                </template>
            </template>
            <div class="placeHolderRight"></div>
        </WorkScheduleContainerColumn>
    </WorkScheduleContainerRow>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {RenderedDay} from '@/types/types';
    import WorkScheduleContainerColumn from '@/components/WorkSchedule/WorkScheduleContainerColumn.vue';
    import WorkScheduleContainerRow from '@/components/WorkSchedule/WorkScheduleContainerRow.vue';
    import Dates from '@cquential/date-utils';

    @Component({
        components: {
            WorkScheduleContainerColumn,
            WorkScheduleContainerRow,
        },
    })
    export default class TimeBar extends Vue {
        @Prop() public label?: string;
        @Prop({default: null}) public activeDay!: Date | null;
        @Prop({required: true}) public renderedDays!: Record<string, RenderedDay>;

        public getHoursInDay(day: RenderedDay): number {
            return Object.values(day.columns).length;
        }

        public dayCssClass(day: RenderedDay): Record<string, boolean> {
            return {
                dayBlock: true,
                activeDay: this.activeDay !== null && Dates.compare(this.activeDay!).isSameDay(day.date),
            };
        }

        public get hasRenderedDays(): boolean {
            return Object.keys(this.renderedDays).length > 0;
        }
    }
</script>

<style lang="scss" scoped>
    .timeBar {
        display: grid;
        grid-auto-flow: column;
        grid-template-rows: repeat(3, auto);
        grid-template-columns: repeat(var(--roster-date-range-in-hours), var(--hour-cell-width));
        text-align: center;

        .placeHolderLeft,
        .placeHolderRight {
            grid-row: 1 / -1;
            background: var(--work-schedule-border-color);
        }

        .placeHolderLeft {
            grid-column: span var(--left-space-holder-hours);
            display: var(--left-place-holder-display);
        }

        .placeHolderRight {
            grid-column: span var(--right-space-holder-hours);
        }

        .dayBlock {
            grid-row: 1;
            grid-column: span var(--day-block-colum-span); /* DST causes days with 23 or 25 hours */
            padding: var(--cell-padding);
            border-top: 1px solid var(--work-schedule-border-color);
            border-left: 1px solid var(--work-schedule-day-border-color);
            border-right-width: 0;
            border-bottom-width: 0;
            background: var(--main-background-color);

            &:nth-child(2) {
                border-left-width: 0;
            }

            span {
                position: sticky;
                left: 10rem;
                padding-left: 0.25rem;
            }

            &.activeDay {
                background: var(--work-schedule-active-day-background-color);
                color: var(--main-background-color);
            }
        }

        .timeBlock {
            width: var(--hour-cell-width);
            padding: var(--cell-padding);
            border: 1px solid var(--work-schedule-border-color);
            border-right-width: 0;
            background: var(--main-background-color);

            &.startOfDay {
                border-left: solid 1px var(--work-schedule-day-border-color);
            }

            &:nth-child(3), &:nth-child(4) {
                border-left-width: 0;
            }

            &:nth-last-child(2), &:nth-last-child(3) {
                border-right-width: 1px;
            }
        }

        .start {
            grid-row: 2;
        }

        .end {
            border-top-width: 0;
            box-shadow: var(--td-bottom-border-shadow) + var(--published-header-color);
            padding-bottom: calc(var(--cell-padding) + 4px); // 4px = 2/3th of border shadow width
            grid-row: 3;
        }

        @media only screen and (width <= 576px) {
            .time-block {
                &:nth-of-type(6n + 2),
                &:nth-of-type(6n + 3),
                &:nth-of-type(6n + 4),
                &:nth-of-type(6n + 5) {
                    display: none;
                }
            }
        }
    }
</style>
